import type { ReactNode } from 'react';
import styles from './AuthLayout.module.css';

/**
 * Shared admin auth-surface chrome — extracted from LoginPage in
 * AAP.2-prep. Closes the chrome-duplication problem before AAP.2 +
 * AAP.3 ship four more auth pages (MagicLink, MagicLinkVerify,
 * ForgotPassword, ResetPassword) that would otherwise each rebuild
 * the same .page + .heroBg + .card + .brandRow scaffolding.
 *
 * Prop contract mirrors the PWA's AuthLayout
 * (apps/web/src/components/AuthLayout.tsx) so the upcoming admin
 * auth pages can copy PWA shapes with minimal adaptation.
 * Difference: admin gains an optional `tagline` prop for the
 * "CHARGING STATION MANAGEMENT" descriptor that sits between the
 * title and subtitle on LoginPage. The other admin auth pages
 * (MagicLink, Forgot, etc.) won't pass it.
 *
 * Visual identity (animated card glow, dark hero background,
 * full-lockup logo lockup) is preserved verbatim from LoginPage's
 * pre-extraction shape — pixel-equivalent.
 */
interface AuthLayoutProps {
  title: string;
  /** Admin extension over PWA's AuthLayout. Optional uppercase
   *  Bebas-Neue descriptor that sits between title + subtitle.
   *  LoginPage passes "Charging station management"; other auth
   *  pages omit it. */
  tagline?: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export function AuthLayout({
  title,
  tagline,
  subtitle,
  children,
  footer,
}: AuthLayoutProps) {
  return (
    <div className={styles.page}>
      <div className={styles.heroBg} />
      <div className={styles.card}>
        <div className={styles.brandRow}>
          <img
            src="/evi-logo.png"
            alt="EV Initiative"
            className={styles.logo}
          />
        </div>
        <h1 className={styles.title}>{title}</h1>
        {tagline && <p className={styles.titleTagline}>{tagline}</p>}
        {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>
  );
}
